import type React from "react";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { PAGE_VIEWPORT, inputPosition, isLiveViewHost, remotePoint } from "./cdp";
import { openTransport, type LiveTransport } from "./transport";

/** How far a finger moves before a touch is a drag, not a tap. */
const TAP_SLOP = 6;
/** Reconnects tried in a row before the view gives up. */
const MAX_RETRIES = 6;
/** Room kept under a tapped spot above the keyboard: the field around it. */
const FIELD_ROOM = 56;

type Status = "connecting" | "live" | "reconnecting" | "failed";

/**
 * The buyer's own view of the browser their purchase runs in, for their
 * bank's check (3-D Secure): what the browser sends drawn on a canvas, their
 * finger and keyboard passed back (transport.ts): over CDP to Browser Use's
 * Chrome (cdp.ts), or to the live view on a Boat browser's machine, which
 * shows the bank's frame alone where there is one (boat.ts).
 *
 * - A drag scrolls the remote page; a still finger is a tap, clicked where
 *   it landed.
 * - iOS raises the keyboard only for a focus made synchronously in the
 *   tap's own touchend (or click): the hidden field takes focus there,
 *   placed where the finger lifted so iOS scrolls that spot above the
 *   keyboard, and gives focus up if the tap focused nothing to type into
 *   (a field inside a frame counts; the frame itself, a tick box or a
 *   button does not). With `keyboard` off, taps never raise it.
 *   The frame is drawn at full width and never scaled down when the
 *   keyboard takes room: the box scrolls instead.
 * - Where the keyboard covers the tapped spot, the box takes room under
 *   the frame and scrolls the spot above the keyboard itself. The browser
 *   would not: the view is often in something fixed (a sheet, the app's
 *   bare page), which scrolling the page does not move.
 */
export function LiveBrowserView({
  host,
  style,
  onRemoteSize,
  keyboard: raisesKeyboard = true,
}: {
  /** The browser's live-view credential (Attempt.liveView.host): Browser
   *  Use's CDP host, or a Boat browser's live view's gated address. */
  host: string;
  /** The scrolling box's size; the frame fills its width, at the remote
   *  page's shape. Give it room for the whole frame: a drag scrolls the
   *  remote page, not this box, so what it cuts off is out of reach. */
  style?: React.CSSProperties;
  /** The remote page's viewport, in its pixels, when it changes: a host
   *  sizes its room to the frame's shape (the app's web view). */
  onRemoteSize?: (size: { width: number; height: number }) => void;
  /** Whether a tap on a field raises the keyboard (the bank's check). Off
   *  for a check that is only tapped: Skiddle's anti-bot tick box. */
  keyboard?: boolean;
}) {
  const canvas = useRef<HTMLCanvasElement>(null);
  const keys = useRef<HTMLInputElement>(null);
  const box = useRef<HTMLDivElement>(null);
  const connection = useRef<LiveTransport | null>(null);
  const remote = useRef<{ width: number; height: number }>({ ...PAGE_VIEWPORT });
  const [status, setStatus] = useState<Status>("connecting");
  const valid = isLiveViewHost(host);
  const sized = useRef(onRemoteSize);
  sized.current = onRemoteSize;

  useEffect(() => {
    if (!valid) return;
    let stopped = false;
    let told = false;
    let retries = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const draw = (data: string, type: string) => {
      const image = new Image();
      image.onload = () => {
        const c = canvas.current;
        if (!c) return;
        if (c.width !== image.width || c.height !== image.height) {
          c.width = image.width;
          c.height = image.height;
        }
        c.getContext("2d")?.drawImage(image, 0, 0);
      };
      image.src = `data:image/${type};base64,${data}`;
    };
    const retry = () => {
      if (stopped) return;
      connection.current = null;
      if (retries >= MAX_RETRIES) return setStatus("failed");
      setStatus("reconnecting");
      timer = setTimeout(connect, Math.min(8000, 500 * 2 ** retries++));
    };
    function connect() {
      openTransport(host, {
        ratio: Math.min(window.devicePixelRatio || 2, 3),
        onFrame: (frame) => {
          const { width, height } = remote.current;
          remote.current = { width: frame.width, height: frame.height };
          if (frame.width !== width || frame.height !== height || !told) {
            told = true;
            sized.current?.(remote.current);
          }
          draw(frame.data, frame.type ?? "jpeg");
        },
        // The browser is the buyer's until the check is over: back again.
        onClose: retry,
      }).then(
        (opened) => {
          if (stopped) return opened.close();
          retries = 0;
          connection.current = opened;
          setStatus("live");
        },
        retry,
      );
    }
    connect();
    return () => {
      stopped = true;
      clearTimeout(timer);
      connection.current?.close();
      connection.current = null;
    };
  }, [host, valid]);

  const pointAt = (clientX: number, clientY: number) => {
    const c = canvas.current;
    return c ? remotePoint(c.getBoundingClientRect(), remote.current, clientX, clientY) : null;
  };

  const touching = useRef(false);
  const moved = useRef(false);
  const start = useRef({ x: 0, y: 0 });
  // Where the finger went down, in the remote page: a tap is clicked there.
  const downAt = useRef<{ x: number; y: number } | null>(null);
  const lastTap = useRef({ x: 0, y: 0 });
  // The tapped spot in the box's content, kept above the keyboard.
  const spot = useRef(0);
  // The keyboard over the box: the room under the frame, and the box's
  // height from before it came (the box scrolls, never grows, into it).
  const [keyboard, setKeyboard] = useState<{ room: number; height: number } | null>(null);
  const reveal = () => {
    const container = box.current;
    const viewport = window.visualViewport;
    if (!container || !viewport || document.activeElement !== keys.current) return;
    const rect = container.getBoundingClientRect();
    const visible = Math.min(rect.bottom, viewport.offsetTop + viewport.height) - rect.top;
    const at = spot.current - container.scrollTop;
    if (at + FIELD_ROOM > visible) container.scrollTop += at + FIELD_ROOM - visible;
    else if (at < FIELD_ROOM) container.scrollTop -= FIELD_ROOM - at;
  };
  useEffect(() => {
    const viewport = window.visualViewport;
    if (!viewport) return;
    const measure = () => {
      const container = box.current;
      if (!container) return;
      const covered =
        document.activeElement === keys.current
          ? Math.max(0, Math.round(container.getBoundingClientRect().bottom - (viewport.offsetTop + viewport.height)))
          : 0;
      setKeyboard((held) =>
        covered === 0
          ? null
          : { room: covered, height: held?.height ?? container.getBoundingClientRect().height },
      );
    };
    viewport.addEventListener("resize", measure);
    viewport.addEventListener("scroll", measure);
    return () => {
      viewport.removeEventListener("resize", measure);
      viewport.removeEventListener("scroll", measure);
    };
  }, []);
  useLayoutEffect(reveal, [keyboard]);
  // Synchronous, inside the tap: see above.
  const focusKeysAtTap = () => {
    const input = keys.current;
    const container = box.current;
    if (!input || !container) return;
    const at = inputPosition(container.getBoundingClientRect(), container.scrollTop, lastTap.current);
    input.style.top = `${at.top}px`;
    input.style.left = `${at.left}px`;
    spot.current = at.top;
    input.focus();
    // Another field while the keyboard is up: nothing resizes.
    requestAnimationFrame(reveal);
  };
  const onPointerDown = (event: React.PointerEvent) => {
    (event.target as Element).setPointerCapture?.(event.pointerId);
    touching.current = true;
    moved.current = false;
    start.current = { x: event.clientX, y: event.clientY };
    downAt.current = pointAt(event.clientX, event.clientY);
    if (downAt.current) connection.current?.drag("start", downAt.current);
  };
  const onPointerMove = (event: React.PointerEvent) => {
    if (!touching.current) return;
    if (
      Math.abs(event.clientX - start.current.x) > TAP_SLOP ||
      Math.abs(event.clientY - start.current.y) > TAP_SLOP
    )
      moved.current = true;
    const point = pointAt(event.clientX, event.clientY);
    if (point) connection.current?.drag("move", point);
  };
  const onPointerUp = () => {
    if (!touching.current) return;
    touching.current = false;
    const at = moved.current ? null : downAt.current;
    const open = connection.current;
    if (!open) {
      if (at) keys.current?.blur();
      return;
    }
    if (!at) return open.drag("end");
    void (async () => {
      // The keyboard stays only for something that takes text.
      const editable = await open.tap(at, raisesKeyboard).catch(() => false);
      if (raisesKeyboard && !editable) keys.current?.blur();
    })();
  };
  const onKeyInput = (event: React.FormEvent<HTMLInputElement>) => {
    const text = event.currentTarget.value;
    event.currentTarget.value = "";
    if (text) connection.current?.type(text);
  };
  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key !== "Backspace" && event.key !== "Enter") return;
    event.preventDefault();
    connection.current?.key(event.key);
  };

  if (!valid) return null;
  return (
    <div
      ref={box}
      data-testid="live-browser-view"
      style={{
        position: "relative",
        background: "#fff",
        overflowX: "hidden",
        overflowY: "auto",
        ...style,
        ...(keyboard ? { height: keyboard.height } : null),
      }}
    >
      <canvas
        ref={canvas}
        aria-label="Your bank's check"
        width={PAGE_VIEWPORT.width}
        height={PAGE_VIEWPORT.height}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onTouchEnd={(event) => {
          const t = event.changedTouches[0];
          if (t) lastTap.current = { x: t.clientX, y: t.clientY };
          if (raisesKeyboard && !moved.current) focusKeysAtTap();
        }}
        onClick={(event) => {
          lastTap.current = { x: event.clientX, y: event.clientY };
          if (raisesKeyboard && !moved.current) focusKeysAtTap();
        }}
        style={{ width: "100%", height: "auto", display: "block", touchAction: "none" }}
      />
      {keyboard ? <div aria-hidden="true" style={{ height: keyboard.room }} /> : null}
      <input
        ref={keys}
        aria-hidden="true"
        tabIndex={-1}
        onInput={onKeyInput}
        onKeyDown={onKeyDown}
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="off"
        spellCheck={false}
        // 16px or larger, or iOS zooms the page when it takes focus. It sits
        // where the last tap landed, so no finger may land on it instead of
        // the frame: the next tap on the same spot would never reach the page.
        style={{ position: "absolute", top: 0, left: 0, width: 1, height: 1, opacity: 0, fontSize: 16, pointerEvents: "none" }}
      />
      {status === "live" ? null : (
        <div
          role="status"
          style={{
            position: "absolute",
            inset: 0,
            display: "grid",
            placeItems: "center",
            padding: 24,
            textAlign: "center",
            color: "#666",
            fontSize: 14,
            pointerEvents: "none",
          }}
        >
          {status === "failed"
            ? "We lost the connection to your bank's check. Check your banking app to approve the payment."
            : status === "reconnecting"
              ? "Reconnecting…"
              : "Loading your bank's check…"}
        </div>
      )}
    </div>
  );
}
